/**
 * Retry-with-backoff for state-changing calls (FR-OFF-005): order submit and
 * gift send are not wired through TanStack Query's mutation cache (they run
 * as direct facade calls from `features/session/session-context.tsx`), so
 * this is a standalone helper rather than a `useMutation` retry option.
 *
 * ASSUMPTION (CLAUDE.md ASSUMPTION protocol): this retries in-memory, within
 * the current page session only. It does NOT persist a durable IndexedDB
 * queue (FR-OFF-006) — a full webview-reload-survives-the-queue durable
 * queue is out of MVP scope for this pass; a killed/reloaded webview loses
 * an in-flight retry the same way it always has. The existing
 * `Idempotency-Key` (already generated once per call site and reused across
 * every retry attempt here, since we re-invoke the same closure) keeps
 * retries safe against the P0 duplicate-prevention requirement either way.
 */

import { isRecoverableFailure } from "./connectivity-state.ts";
import { connectivityStore } from "./connectivity-store.ts";

export interface RetryWithBackoffOptions {
  /** Bounded retry window (FR-OFF-013: no silent infinite retry). */
  maxAttempts?: number;
  /** Base delay in ms (FR-OFF-005 default: base 1s, factor 2, cap 30s, full jitter). */
  baseDelayMs?: number;
  factor?: number;
  capDelayMs?: number;
  isRecoverable?: (error: unknown) => boolean;
  /** Called after each failed attempt that will be retried (not on the final give-up). */
  onRetry?: (error: unknown, attempt: number) => void;
}

const DEFAULT_MAX_ATTEMPTS = 4;
const DEFAULT_BASE_DELAY_MS = 1000;
const DEFAULT_FACTOR = 2;
const DEFAULT_CAP_DELAY_MS = 30_000;

export function backoffDelayMs(
  attemptIndex: number,
  baseDelayMs = DEFAULT_BASE_DELAY_MS,
  factor = DEFAULT_FACTOR,
  capDelayMs = DEFAULT_CAP_DELAY_MS,
): number {
  const upperBound = Math.min(capDelayMs, baseDelayMs * factor ** attemptIndex);
  // Full jitter (FR-OFF-005): uniform random in [0, upperBound].
  return Math.random() * upperBound;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Runs `fn`, retrying on recoverable (network-type) failures only — never on
 * an application-level 4xx (FR-OFF-005/009) — with exponential backoff + full
 * jitter, up to a bounded number of attempts (FR-OFF-013). Every outcome is
 * reported to the shared connectivity store so the banner/state model stays
 * in sync with real request results, not just `navigator.onLine`.
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryWithBackoffOptions = {},
): Promise<T> {
  const {
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    baseDelayMs = DEFAULT_BASE_DELAY_MS,
    factor = DEFAULT_FACTOR,
    capDelayMs = DEFAULT_CAP_DELAY_MS,
    isRecoverable = isRecoverableFailure,
    onRetry,
  } = options;

  let attempt = 0;
  for (;;) {
    try {
      const result = await fn();
      connectivityStore.dispatch({ type: "request-success" });
      return result;
    } catch (error) {
      attempt += 1;
      const recoverable = isRecoverable(error);
      connectivityStore.dispatch({ type: "request-failure", recoverable });

      if (!recoverable || attempt >= maxAttempts) {
        throw error;
      }
      onRetry?.(error, attempt);
      await delay(backoffDelayMs(attempt - 1, baseDelayMs, factor, capDelayMs));
    }
  }
}
